import { useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { CalendarIcon, CalendarCheck } from "lucide-react";
import { Button, type ButtonProps } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { submitCrmLead } from "@/lib/leads";
import { THANK_YOU } from "@/lib/thankYou";
import { useToast } from "@/hooks/use-toast";
import { trackRegistrationCompleted } from "@/lib/openaiPixel";

const TIME_SLOTS = [
  "09:00", "10:00", "11:00", "12:00", "13:00",
  "14:00", "15:00", "16:00", "17:00", "18:00", "19:00",
];

export interface ScheduleVisitFormProps {
  /** Property / page the visit refers to. Stored in the CRM "Agenda" segment. */
  propertyName?: string;
  neighborhood?: string | null;
  /** Applies light-on-dark styles (for use over the secondary/navy background). */
  onDark?: boolean;
  submitLabel?: string;
  onDone?: () => void;
  /** Thank-you page used for conversion tracking (defaults to the visit funnel). */
  thankYouPath?: string;
}

/** Visit scheduling form: name, WhatsApp, e-mail, preferred day (calendar) and time. */
export const ScheduleVisitForm = ({
  propertyName,
  neighborhood,
  onDark = false,
  submitLabel = "Quero agendar minha visita",
  onDone,
  thankYouPath = THANK_YOU.visita,
}: ScheduleVisitFormProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [date, setDate] = useState<Date | undefined>();
  const [time, setTime] = useState("");
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);

  const fieldClass = onDark
    ? "bg-secondary-foreground/5 border-secondary-foreground/10 text-secondary-foreground placeholder:text-secondary-foreground/30"
    : "";
  const labelClass = onDark ? "text-secondary-foreground/70" : "text-foreground";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim()) {
      toast({ title: "Preencha nome e WhatsApp", variant: "destructive" });
      return;
    }
    setSaving(true);
    const visitDate = date
      ? new Date(`${format(date, "yyyy-MM-dd")}T${time || "10:00"}:00`).toISOString()
      : undefined;
    const { error } = await submitCrmLead({
      name,
      phone,
      email,
      propertyName: `Agenda: ${propertyName || "Visita agendada pelo site"}`,
      neighborhood: neighborhood || null,
      temperature: "quente",
      visitDate,
      notes: [
        date ? `Dia preferido para a visita: ${format(date, "dd/MM/yyyy", { locale: ptBR })}` : "",
        time ? `Horário preferido: ${time}` : "",
        message.trim() ? `Mensagem: ${message.trim()}` : "",
      ],
    });
    setSaving(false);
    if (error) {
      toast({ title: "Erro ao enviar", description: error.message, variant: "destructive" });
      return;
    }
    // ChatGPT Ads conversion backup (in case redirect fails or is delayed)
    trackRegistrationCompleted({ propertyName, funnel: "schedule_visit" });
    onDone?.();
    try {
      sessionStorage.setItem("tc_return_to", window.location.pathname);
    } catch {
      /* ignore */
    }
    navigate(thankYouPath);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label className={cn("font-body text-sm mb-1.5 block", labelClass)}>Nome completo *</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} required maxLength={120} className={fieldClass} placeholder="Seu nome" />
        </div>
        <div>
          <Label className={cn("font-body text-sm mb-1.5 block", labelClass)}>WhatsApp *</Label>
          <Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} required maxLength={40} className={fieldClass} placeholder="(21) 99999-9999" />
        </div>
      </div>
      <div>
        <Label className={cn("font-body text-sm mb-1.5 block", labelClass)}>E-mail</Label>
        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={200} className={fieldClass} placeholder="seu@email.com" />
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label className={cn("font-body text-sm mb-1.5 block", labelClass)}>Melhor dia para a visita</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                className={cn("w-full justify-start text-left font-normal", fieldClass, !date && "text-muted-foreground")}
              >
                <CalendarIcon className="h-4 w-4" />
                {date ? format(date, "dd 'de' MMMM", { locale: ptBR }) : "Escolher data"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={date}
                onSelect={setDate}
                locale={ptBR}
                disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                initialFocus
                className={cn("p-3 pointer-events-auto")}
              />
            </PopoverContent>
          </Popover>
        </div>
        <div>
          <Label className={cn("font-body text-sm mb-1.5 block", labelClass)}>Melhor horário</Label>
          <Select value={time} onValueChange={setTime}>
            <SelectTrigger className={fieldClass}>
              <SelectValue placeholder="Escolher horário" />
            </SelectTrigger>
            <SelectContent>
              {TIME_SLOTS.map((h) => (
                <SelectItem key={h} value={h}>{h}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label className={cn("font-body text-sm mb-1.5 block", labelClass)}>Mensagem (opcional)</Label>
        <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} maxLength={1000} className={cn("resize-none", fieldClass)} placeholder="Conte o que você procura ou outra data alternativa" />
      </div>
      <Button type="submit" variant="gold" size="lg" className="w-full text-lg" disabled={saving}>
        <CalendarCheck className="w-5 h-5" />
        {saving ? "Enviando..." : submitLabel}
      </Button>
      <p className={cn("font-body text-xs text-center", onDark ? "text-secondary-foreground/40" : "text-muted-foreground")}>
        Seus dados são usados apenas para o agendamento. Não fazemos spam.
      </p>
    </form>
  );
};

interface ScheduleVisitDialogProps extends ScheduleVisitFormProps {
  /** Button label when no custom trigger is provided. */
  label?: string;
  variant?: ButtonProps["variant"];
  size?: ButtonProps["size"];
  className?: string;
  children?: ReactNode;
}

/** Button + modal with the visit scheduling form. */
const ScheduleVisitDialog = ({
  label = "Agendar visita",
  variant = "gold",
  size = "lg",
  className,
  children,
  ...formProps
}: ScheduleVisitDialogProps) => {
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children ?? (
          <Button variant={variant} size={size} className={className}>
            <CalendarCheck className="w-5 h-5" />
            {label}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Agende sua visita</DialogTitle>
          <DialogDescription className="font-body">
            Escolha o melhor dia e horário. Confirmamos pelo WhatsApp em seguida.
          </DialogDescription>
        </DialogHeader>
        <ScheduleVisitForm {...formProps} onDone={() => setOpen(false)} />
      </DialogContent>
    </Dialog>
  );
};

export default ScheduleVisitDialog;
